/**
 * Teams API Endpoints
 *
 * A team is PEOPLE; a location is a place. A shift belongs to a team's roster,
 * so every shift write names one — which is why this module exists on the
 * client at all: the nurse who presses "start shift" has to say which roster
 * the shift goes onto.
 *
 * The list is what OpenFGA says the caller may view: their own teams, and any
 * they lead. It is never the platform's teams.
 */

import { api } from '../client';
import type { Period } from '../temporal';
import type { Page } from '../types';
import type { Reference } from '@/lib/reference';
import { createLogger } from '@/utils/logger';

const logger = createLogger('TeamsAPI');

/** One staffing unit. `kind` is a code from config `staffing.team_kinds`. */
export interface TeamResponse {
  id: string;
  name: string;
  kind: string;
  /** The places this team staffs. Always present; `[]` means it staffs none. */
  staffed_locations: string[];
  /** The accountable person. `null` is a real state — a team whose lead left. */
  lead: string | null;
  created_at: string;
}

/** One page of teams, in the shared envelope. */
export type TeamListResponse = Page<TeamResponse>;

export interface ListTeamsParams {
  /** AIP-160 filter, e.g. `kind = ward`. */
  filter?: string;
  page_size?: number;
}

/** Every team the caller may view, following the list's `Link: rel="next"`. */
export async function listTeams(
  params: ListTeamsParams = {},
): Promise<TeamResponse[]> {
  const query = new URLSearchParams();
  if (params.filter) query.set('filter', params.filter);
  if (params.page_size !== undefined) query.set('page_size', String(params.page_size));
  const qs = query.toString();

  try {
    logger.debug('Listing teams', params);
    return await api.getAllItems<TeamListResponse>(qs ? `/teams?${qs}` : '/teams');
  } catch (error) {
    logger.error('Failed to list teams', { error });
    throw error;
  }
}

/** One team membership. Mirrors Go TeamMemberResponse. */
export interface TeamMemberResponse {
  id: string;
  team_id: string;
  /** The member's bare user id — the same id `/users/{user_id}/roles` takes. */
  user: string;
  /** The membership's resolved pointer at the person, carrying the display name. */
  user_ref: Reference;
  role: string;
  qualification: string;
  fte: number;
  period: Period;
}

export type TeamMemberListResponse = Page<TeamMemberResponse>;

/** Every member of a team, following the list's `Link: rel="next"`. */
export async function listTeamMembers(teamId: string): Promise<TeamMemberResponse[]> {
  try {
    logger.debug('Listing team members', { teamId });
    return await api.getAllItems<TeamMemberListResponse>(
      `/teams/${encodeURIComponent(teamId)}/members`,
    );
  } catch (error) {
    logger.error('Failed to list team members', { teamId, error });
    throw error;
  }
}

/**
 * The teams that staff a unit. Staffing is a TEAMS question — a location is a
 * place and a team is people — so the unit id is a filter here rather than a
 * path segment on `/locations`.
 */
export async function listTeamsStaffingLocation(locationId: string): Promise<TeamResponse[]> {
  return listTeams({ filter: `staffed_location = ${locationId}` });
}
